import EllipsisTooltip from "components/ui/tooltip/EllipsisTooltip";
import Badge from "lib/core_components/Badge";
import { MultiSelectDropdown, Option } from "lib/core_components/MultiSelectDropdownList";
import { useCompany } from "lib/hooks/api/company/useCompany";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import UIcon from "../../../lib/icons/untitled_ui/UIcon";
import { isDefined, pluralise } from "../../../lib/utils";
import { Flex, Text } from "../../../styles/utility-components";
import UserInitials, { UnassignedIcon } from "../../comments/UserInitials";
import UserName, { formatUserNameWithStatus } from "../../users/UserName";

export type AssigneeFilterValue = { ids: string[]; includeNulls?: boolean };
type User = { guid: string; firstName: string; lastName: string; active: boolean };

type Noun = "assignee" | "owner";
const nounLabels: Record<Noun, string> = {
  assignee: "Assigned to",
  owner: "Owned by",
};

const NO_USER_VALUE = "noUser";
const createNoUserOption = (noun: Noun) => ({
  title: NO_USER_VALUE,
  value: NO_USER_VALUE,
  OptionComponent: (
    <Flex gap={9} alignItems="center" minWidth={200} height={37}>
      <UnassignedIcon showTooltip={false} type="extra-small" />
      <Text fontWeight="500">No {noun}</Text>
    </Flex>
  ),
});

function generateOptions(
  users: User[],
  noun: Noun,
  hideUnassignedOption: boolean,
  currentUserId: string,
): Option<string>[] {
  const userOptions = users.map((user) => ({
    title: formatUserNameWithStatus(user),
    value: user.guid,
    OptionComponent: (
      <Flex gap={9} alignItems="center" key={user.guid} minWidth={200} height={37}>
        <UserInitials
          firstName={user?.firstName}
          lastName={user?.lastName}
          showTooltip={false}
          type="extra-small"
        />
        <UserName user={user} fontWeight="500" />
      </Flex>
    ),
  }));

  const userId = currentUserId;
  // bring the current user to the top of the list and unassigned to the second in the list
  const currentUser = userOptions.find((user) => user.value === userId);
  const noUserOption = hideUnassignedOption ? null : createNoUserOption(noun);

  if (!currentUser) {
    return [noUserOption, ...userOptions].filter(isDefined);
  }

  return [currentUser, noUserOption, ...userOptions.filter((user) => user.value !== userId)].filter(
    isDefined,
  );
}

type UserDropdownFilterProps = {
  onChange: (v: AssigneeFilterValue) => void;
  value: AssigneeFilterValue;
  options?: {
    noun?: Noun;
    hideUnassignedOption?: boolean;
  };
  minified?: boolean;
};
export function UserDropdownFilter({
  onChange,
  value,
  options = {},
  minified = false,
}: UserDropdownFilterProps) {
  const currentUser = useAssertCurrentUser();
  const { noun = "assignee", hideUnassignedOption = false } = options;

  const { data: company, isLoading: isLoadingCompany } = useCompany({ includeInactive: true });

  const onChangeWrapper = (values: string[]) => {
    const selectedUsers = values.filter((user) => user !== NO_USER_VALUE);
    const includeUnassigned = !!values.find((user) => user === NO_USER_VALUE);
    return onChange({
      ids: selectedUsers,
      includeNulls: includeUnassigned,
    });
  };

  const valueCombined = [...value.ids, ...(value.includeNulls ? [NO_USER_VALUE] : [])];

  const companyUsers = company?.users ?? [];
  const label = getSelectDropdownLabel(value, companyUsers, currentUser.guid, noun);

  return (
    <MultiSelectDropdown
      title={
        <>
          <UIcon icon="user01" color="sysPrimaryDefault" />
          {!minified && (
            // Only fires when the name is actually cut off, so the everyday labels don't
            // get a tooltip repeating what is already on screen.
            <EllipsisTooltip fullText={label}>
              {(ref) => (
                <Text ref={ref} color="inherit" ellipsis>
                  {label}
                </Text>
              )}
            </EllipsisTooltip>
          )}
          {minified && valueCombined.length > 0 && <Badge size={16}>{valueCombined.length}</Badge>}
        </>
      }
      fieldName={options.noun}
      searchable
      hideSelectAll
      skipSort
      options={generateOptions(companyUsers, noun, hideUnassignedOption, currentUser.guid)}
      selectedOptions={valueCombined}
      onChange={onChangeWrapper}
      loading={isLoadingCompany}
      showSelectedSizeBadge={false}
      hideChevron={minified}
    />
  );
}

function getSelectDropdownLabel(
  selectedValue: AssigneeFilterValue,
  allUsers: User[],
  currentUserId: string,
  noun: Noun,
): string {
  const { ids, includeNulls: unassignedSelected } = selectedValue;
  const label = nounLabels[noun];

  const numSelectedUsers = ids.length;
  const totalUsers = allUsers.length;

  const getUserFullNameById = (id: string) => {
    const user = allUsers.find((u) => u.guid === id);
    if (!user) return null;
    return formatUserNameWithStatus(user);
  };

  if (unassignedSelected) {
    if (numSelectedUsers === 0) return `No ${noun}`;
    if (numSelectedUsers < totalUsers) return pluralise(numSelectedUsers + 1, noun);
    return `${label} anyone`;
  }

  if (numSelectedUsers === 0) return `${label} anyone`;

  if (numSelectedUsers === 1) {
    return ids[0] === currentUserId ? `${label} me` : `${label} ${getUserFullNameById(ids[0])}`;
  }

  if (numSelectedUsers < totalUsers) {
    return pluralise(numSelectedUsers, noun);
  }

  return `${label} anyone`;
}
